import { useSaveSettings } from '@ais-ui/websockets';
import { useLayerOrderingMirror } from '@ais-ui/user-settings';
import { useSettings } from '../../../Settings/SettingsProvider';
import {
  createLayerOrderingFromFavorites,
  LayerConfig,
} from './layerLoadingUtils';
import { useWMSSettings } from '../../../WMSLayers/useWMSSettings';
import { useCallback } from 'react';
import { useToast } from '@ais-ui/ui-core';
import { isLayerFavorite } from './layerFavoriteCore';
import { getVectorTilesFromSettings } from '../../../VectorTiles/useVectorTileSettings';

export const useLayerOrderingSettings = () => {
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const { enabledWmsLayers } = useWMSSettings();
  const mirrorOrdering = useLayerOrderingMirror();

  const saveLayerOrderingToSettings = useCallback(
    (layerOrdering: LayerConfig[]) => {
      const settingsToSave = {
        ...settings,
        'ribbonPlugin.settings': {
          ...settings['ribbonPlugin.settings'],
          layer_ordering: {
            version: 1,
            layers: layerOrdering,
          },
        },
      };

      // Blob stays authoritative; only after it is written do we mirror the
      // ordering to the user-settings service (no-op in phase 1, best-effort
      // in phase >= 2) — matching the favorite-toggle path.
      mutateSettings(settingsToSave, {
        onSuccess: () => mirrorOrdering(layerOrdering),
      });
    },
    [settings, mutateSettings, mirrorOrdering],
  );

  const createInitialLayerOrdering = useCallback(() => {
    return createLayerOrderingFromFavorites(settings, enabledWmsLayers);
  }, [settings, enabledWmsLayers]);

  const getExistingLayerOrdering = useCallback((): LayerConfig[] | null => {
    // Migrate legacy 'ships' type to 'vessels' for backward compatibility
    const ordering = (
      settings['ribbonPlugin.settings']?.layer_ordering?.layers || []
    ).map((layer: LayerConfig) =>
      layer.type === 'ships' ? { ...layer, type: 'vessels' } : layer,
    );
    if (!ordering || ordering.length === 0) {
      return null;
    }

    // Get current favorites
    const favoriteShapes =
      settings['ribbonPlugin.settings']?.favorite_shapes?.shapes || [];

    const filteredOrdering = ordering.filter((layer: LayerConfig) => {
      // Always keep ships layer
      if (layer.type === 'vessels') return true;

      // Check if shape is still in favorites
      if (layer.type === 'shape') {
        return favoriteShapes.includes(layer.id);
      }

      // Check if WMS layer is still enabled
      if (layer.type === 'wms') {
        const wmsLayer = enabledWmsLayers.find(
          (wms: any) => wms.wmsLayerInfo.id === layer.id,
        );
        return !!wmsLayer;
      }

      // Check if coverage is still enabled
      if (layer.type === 'coverage' && layer.id === 'source-identifiers') {
        return isLayerFavorite(settings, 'source-identifiers');
      }

      // Keep vector tile layers that are still favorited
      if (layer.type === 'vectortile') {
        return isLayerFavorite(settings, layer.id);
      }

      // Keep OpenSeaMap if still favorited
      if (layer.type === 'openseamap') {
        return isLayerFavorite(settings, layer.id);
      }

      // Keep radar layers that are still favorited
      if (layer.type === 'cambridge-radar') {
        return isLayerFavorite(settings, layer.id);
      }

      return false;
    });

    // Find missing favorite layers that aren't in the current ordering
    const missingFavorites: LayerConfig[] = [];

    // Check for missing favorite shapes
    favoriteShapes.forEach((shapeId: string) => {
      const exists = filteredOrdering.some(
        (layer: LayerConfig) => layer.type === 'shape' && layer.id === shapeId,
      );
      if (!exists) {
        missingFavorites.push({
          type: 'shape',
          id: shapeId,
          enabled: true,
        });
      }
    });

    // Check for missing WMS layers
    enabledWmsLayers.forEach((wms: any) => {
      const exists = filteredOrdering.some(
        (orderingLayer: LayerConfig) =>
          orderingLayer.type === 'wms' &&
          orderingLayer.id === wms.wmsLayerInfo.id,
      );

      if (!exists) {
        missingFavorites.push({
          type: 'wms',
          id: wms.wmsLayerInfo.id,
          enabled: true,
        });
      }
    });

    // Check for missing coverage layer
    if (isLayerFavorite(settings, 'source-identifiers')) {
      const exists = filteredOrdering.some(
        (orderingLayer: LayerConfig) =>
          orderingLayer.type === 'coverage' &&
          orderingLayer.id === 'source-identifiers',
      );
      if (!exists) {
        missingFavorites.push({
          type: 'coverage',
          id: 'source-identifiers',
          enabled: true,
        });
      }
    }

    // Check for missing favorite vector tiles
    const vectorTiles = getVectorTilesFromSettings(settings);
    vectorTiles.forEach((vt: any) => {
      if (vt.enabled) {
        const exists = filteredOrdering.some(
          (orderingLayer: LayerConfig) =>
            orderingLayer.type === 'vectortile' && orderingLayer.id === vt.id,
        );
        if (!exists) {
          missingFavorites.push({
            type: 'vectortile',
            id: vt.id,
            enabled: true,
          });
        }
      }
    });

    // Check for missing OpenSeaMap
    if (isLayerFavorite(settings, 'openseamap-seamark')) {
      const exists = filteredOrdering.some(
        (orderingLayer: LayerConfig) => orderingLayer.type === 'openseamap',
      );
      if (!exists) {
        missingFavorites.push({
          type: 'openseamap',
          id: 'openseamap-seamark',
          enabled: true,
        });
      }
    }

    // Check for missing favorite radar layers
    const radarSources = settings?.radarSettings || [];
    radarSources.forEach((r: any) => {
      if (isLayerFavorite(settings, r.id)) {
        const exists = filteredOrdering.some(
          (orderingLayer: LayerConfig) =>
            orderingLayer.type === 'cambridge-radar' &&
            orderingLayer.id === r.id,
        );
        if (!exists) {
          missingFavorites.push({
            type: 'cambridge-radar',
            id: r.id,
            enabled: true,
          });
        }
      }
    });

    // Insert missing favorites BEFORE ships-layer to keep them in the
    // "below-ships" zone. Layers after ships-layer in the array are
    // loaded at 'above-ships' position, which would give them a higher
    // z-index than Vessels — the wrong position for newly discovered favorites.
    if (missingFavorites.length > 0) {
      const shipsIndex = filteredOrdering.findIndex(
        (l: LayerConfig) => l.type === 'vessels',
      );
      if (shipsIndex === -1) {
        return [...filteredOrdering, ...missingFavorites];
      }
      return [
        ...filteredOrdering.slice(0, shipsIndex),
        ...missingFavorites,
        ...filteredOrdering.slice(shipsIndex),
      ];
    }

    return filteredOrdering;
  }, [settings, enabledWmsLayers]);

  return {
    settings, // Return settings so other hooks can use them
    saveLayerOrderingToSettings,
    createInitialLayerOrdering,
    getExistingLayerOrdering,
  };
};

// Hook for getting favorite layer data
export const useFavoriteLayerData = () => {
  const { settings } = useSettings();
  const { enabledWmsLayers } = useWMSSettings();

  const favoriteShapes =
    settings['ribbonPlugin.settings']?.favorite_shapes?.shapes || [];
  const favoriteFilters =
    settings['ribbonPlugin.settings']?.favorite_filters?.filters || [];

  return {
    favoriteShapes,
    favoriteFilters,
    enabledWmsLayers,
  };
};

// Hook for shape management utilities
export const useShapeManagement = () => {
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const { toast } = useToast();

  const removeShapeFromFavorites = useCallback(
    (shapeIdToRemove: string) => {
      const settingsToSave = {
        ...settings,
        'ribbonPlugin.settings': {
          ...settings['ribbonPlugin.settings'],
          favorite_shapes: {
            ...settings['ribbonPlugin.settings']?.favorite_shapes,
            shapes:
              settings[
                'ribbonPlugin.settings'
              ]?.favorite_shapes?.shapes?.filter(
                (id: string) => id !== shapeIdToRemove,
              ) || [],
          },
        },
      };

      mutateSettings(settingsToSave, {
        onSuccess: () => {
          toast({
            className: 'update-start-up-shapes-success-toast',
            title: 'Success!',
            description: `Removed invalid shape from favorites`,
          });
        },
        onError: () => {
          toast({
            className: 'update-start-up-shapes-settings-error-toast',
            title: 'Error!',
            description: 'Failed to update favorite shapes settings',
            variant: 'destructive',
          });
        },
      });
    },
    [settings, mutateSettings, toast],
  );

  return {
    removeShapeFromFavorites,
  };
};
